/**
 * AddArticleModal — athlete-facing "paste any article URL, we'll pull
 * the metadata" flow. Fetches OpenGraph headline + thumbnail + source
 * via /api/me/athlete/article-preview, then saves to the athlete's
 * `press` array via /api/me/athlete/profile.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Link2, CheckCircle2, Search, Newspaper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_ORIGIN = process.env.REACT_APP_PUBLIC_ORIGIN;

const AddArticleModal = ({ open, onClose, existingPress = [], onSaved }) => {
  // iter516m · Two-tab flow — paste an external URL OR search stories
  // already published on Flythouse and pick one. Athletes love adding
  // their own FLYT-published stories to their press wall so the wall
  // has content from day one, no external outlet needed.
  const [tab, setTab] = useState('url');   // 'url' | 'flyt'
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // FLYT-tab state
  const [query, setQuery] = useState('');
  const [flytStories, setFlytStories] = useState(null);   // null = not fetched yet
  const [flytLoading, setFlytLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setUrl(''); setPreview(null); setTab('url'); setQuery('');
      setFlytStories(null);
    }
  }, [open]);

  // Fetch stories once when the FLYT tab is first opened. Cached for the
  // life of the modal — closing/reopening refetches (data stays fresh).
  useEffect(() => {
    if (!open || tab !== 'flyt' || flytStories !== null) return;
    setFlytLoading(true);
    axios.get(`${API}/stories`, { params: { limit: 100, sort: 'recent' } })
      .then((r) => setFlytStories(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFlytStories([]))
      .finally(() => setFlytLoading(false));
  }, [open, tab, flytStories]);

  const filteredFlyt = (() => {
    if (!flytStories) return [];
    const q = query.trim().toLowerCase();
    if (!q) return flytStories.slice(0, 30);
    return flytStories.filter((s) => {
      const hay = [s.name, s.subtitle, s.category, s.sport, s.league]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    }).slice(0, 30);
  })();

  const addFromFlyt = async (story) => {
    // iter516m · Compose the same `press` entry shape used by pasted
    // URLs so the wall renders identically regardless of source.
    const item = {
      url: `${PUBLIC_ORIGIN}/stories/${story.slug || story.id}`,
      headline: story.name || 'Untitled',
      outlet: 'FLYT League',
      thumbnail: story.cover_image || story.thumbnail || '',
    };
    setSaving(true);
    try {
      const nextList = [...(existingPress || []), item];
      const r = await axios.put(
        `${API}/me/athlete/profile`,
        { updates: { press: nextList } },
        { withCredentials: true },
      );
      toast.success(`Added "${item.headline.slice(0, 40)}…"`);
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const fetchPreview = async () => {
    if (!url.trim() || !/^https?:\/\//i.test(url.trim())) {
      toast.error('Paste a full https:// URL.');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/me/athlete/article-preview`, {
        params: { url: url.trim() },
        withCredentials: true,
      });
      setPreview(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed. You can still save the URL manually.');
      setPreview({ url: url.trim(), headline: '', outlet: '', thumbnail: '' });
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    const item = preview || { url: url.trim(), headline: '', outlet: '', thumbnail: '' };
    if (!item.url) return;
    setSaving(true);
    try {
      const nextList = [...(existingPress || []), item];
      const r = await axios.put(
        `${API}/me/athlete/profile`,
        { updates: { press: nextList } },
        { withCredentials: true },
      );
      toast.success('Article added');
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-3 pb-3 sm:pb-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="add-article-modal"
    >
      <div
        className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-[#0b0b10] ring-1 ring-white/10 shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 16px)' }}
      >
        <div className="p-5 sm:p-6 overflow-y-auto" style={{ paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px))' }}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-blue-300 font-bold">Add article</p>
              <h2 className="font-display text-2xl uppercase tracking-tight text-white leading-tight mt-1">
                {tab === 'url' ? 'Paste any article URL' : 'Add from FLYT stories'}
              </h2>
              <p className="text-xs text-white/50 mt-1.5 leading-snug">
                {tab === 'url'
                  ? "We'll grab the headline, thumbnail, and source automatically."
                  : 'Add a Flythouse-published story to your wall in one tap.'}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 w-8 h-8 rounded-full bg-white/[0.06] ring-1 ring-white/10 text-white/70 hover:text-white hover:bg-white/[0.10] flex items-center justify-center transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* iter516m · Tab switcher: External URL vs FLYT-published story */}
          <div className="grid grid-cols-2 gap-1 rounded-full bg-white/[0.04] ring-1 ring-white/10 p-1 mb-4" data-testid="add-article-tabs">
            <button
              type="button"
              onClick={() => setTab('url')}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold transition ${tab === 'url' ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
              data-testid="add-article-tab-url"
            >
              <Link2 className="w-3 h-3" /> External URL
            </button>
            <button
              type="button"
              onClick={() => setTab('flyt')}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold transition ${tab === 'flyt' ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
              data-testid="add-article-tab-flyt"
            >
              <Newspaper className="w-3 h-3" /> FLYT Stories
            </button>
          </div>

          {tab === 'flyt' && (
            <div className="space-y-3" data-testid="add-article-flyt-panel">
              <div className="flex items-center gap-2 rounded-full bg-black/60 ring-1 ring-white/10 px-3 py-2">
                <Search className="w-4 h-4 text-white/40 shrink-0" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search stories by title, athlete, school…"
                  className="flex-1 text-sm bg-transparent focus:outline-none text-white placeholder-white/40"
                  data-testid="add-article-flyt-search"
                />
              </div>

              {flytLoading && (
                <div className="flex items-center justify-center py-10 text-white/50 text-xs gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading stories…
                </div>
              )}

              {!flytLoading && filteredFlyt.length === 0 && (
                <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-6 text-center text-white/40 text-xs">
                  {query.trim() ? 'No stories match that search.' : 'No stories available yet.'}
                </div>
              )}

              {!flytLoading && filteredFlyt.length > 0 && (
                <ul className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="add-article-flyt-results">
                  {filteredFlyt.map((s) => (
                    <li key={s.id || s.slug}>
                      <button
                        type="button"
                        onClick={() => addFromFlyt(s)}
                        disabled={saving}
                        className="w-full flex items-center gap-3 rounded-xl bg-white/[0.04] ring-1 ring-white/10 hover:bg-white/[0.08] hover:ring-white/20 active:scale-[0.99] transition text-left p-2 disabled:opacity-40"
                        data-testid={`add-article-flyt-pick-${s.slug || s.id}`}
                      >
                        <div className="w-14 h-14 rounded-lg bg-black overflow-hidden shrink-0 ring-1 ring-white/10">
                          {(s.thumbnail || s.cover_image) && (
                            <img
                              src={s.thumbnail || s.cover_image}
                              alt=""
                              className="w-full h-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[9px] uppercase tracking-widest text-blue-300 font-bold">FLYT League</p>
                          <p className="text-sm text-white font-semibold leading-tight line-clamp-2 mt-0.5">
                            {s.name || 'Untitled'}
                          </p>
                          {(s.category || s.sport || s.league) && (
                            <p className="text-[10px] text-white/40 mt-0.5">
                              {[s.category, s.sport, s.league].filter(Boolean).join(' · ')}
                            </p>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === 'url' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-white/40 shrink-0" />
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchPreview(); }}
                placeholder="https://espn.com/article/..."
                className="flex-1 text-xs font-mono rounded-lg px-3 py-2 focus:outline-none"
                style={{ color: '#fff', caretColor: '#fff', backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid="add-article-url"
              />
              <button
                onClick={fetchPreview}
                disabled={loading || !url.trim()}
                className="text-[11px] uppercase tracking-widest font-bold text-black bg-white hover:bg-blue-300 rounded-full px-3 py-2 disabled:opacity-40 disabled:hover:bg-white transition"
                data-testid="add-article-fetch"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Preview'}
              </button>
            </div>

            {preview && (
              <div className="rounded-2xl overflow-hidden ring-1 ring-white/10" data-testid="add-article-preview">
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-black">
                  {preview.thumbnail ? (
                    <img
                      src={preview.thumbnail}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
                      style={{
                        background:
                          'radial-gradient(120% 100% at 50% 0%, rgba(35,35,42,0.85) 0%, rgba(10,10,12,0.98) 60%, #05050a 100%)',
                      }}
                    >
                      <p className="text-[10px] uppercase tracking-[0.3em] font-black text-white/45 mb-3">
                        {preview.outlet || 'Article'}
                      </p>
                      <p className="font-display text-base leading-tight text-white/85 line-clamp-3">
                        {preview.headline || 'Headline will appear here'}
                      </p>
                    </div>
                  )}
                  {/* Bottom scrim + title overlay */}
                  <div className="absolute inset-x-0 bottom-0 p-3 pt-12 pointer-events-none bg-gradient-to-t from-black/95 via-black/55 to-transparent">
                    <p className="font-display text-sm leading-tight text-white line-clamp-2 pr-24">
                      {preview.headline || 'Headline'}
                    </p>
                  </div>
                  {/* Source pill — brand attribution. Reads
                      "Powered by FLYT" so the athlete adding an
                      article sees the same footprint that renders
                      publicly on their /network profile. */}
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-[9px] font-black uppercase tracking-widest text-white/90 ring-1 ring-white/15 pointer-events-none">
                    <span className="text-white/50 mr-1">Powered by</span>
                    FLYT
                  </span>
                </div>
                <div className="p-3 space-y-2">
                  <input
                    type="text"
                    value={preview.headline || ''}
                    onChange={(e) => setPreview({ ...preview, headline: e.target.value })}
                    placeholder="Headline (auto-filled)"
                    className="w-full text-sm rounded-lg px-3 py-2 focus:outline-none"
                    style={{ color: '#fff', caretColor: '#fff', backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={preview.outlet || ''}
                      onChange={(e) => setPreview({ ...preview, outlet: e.target.value })}
                      placeholder="Source"
                      className="text-xs rounded-lg px-3 py-2 focus:outline-none"
                      style={{ color: '#fff', caretColor: '#fff', backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}
                    />
                    <input
                      type="text"
                      value={preview.date || ''}
                      onChange={(e) => setPreview({ ...preview, date: e.target.value })}
                      placeholder="Date (YYYY-MM-DD)"
                      className="text-xs rounded-lg px-3 py-2 focus:outline-none"
                      style={{ color: '#fff', caretColor: '#fff', backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          )}{/* /iter516m tab === 'url' */}

          {/* Save row — only visible on URL tab (FLYT tab saves inline on tap) */}
          {tab === 'url' && (
          <div className="flex items-center justify-end gap-2 mt-5">
            <button
              onClick={onClose}
              className="text-[11px] uppercase tracking-widest font-bold text-white/50 hover:text-white/80 px-3 py-2"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={!preview || saving}
              className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2.5 rounded-full bg-white text-black hover:bg-blue-300 transition disabled:opacity-40 disabled:hover:bg-white"
              data-testid="add-article-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {saving ? 'Saving…' : 'Save article'}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddArticleModal;
